import { useCallback } from 'react';
import { useMediaResource } from '@brightmls/nextgen.lae.flc';
import {
  useMediaStore,
  useMediaStoreReadonly,
} from '@brightmls/nextgen.lae.mediacenter';
import { MediaCategory } from '../../../../system/media';
import { useMediaLogger } from './useMediaLogger';

/**
 * Hook that provides a function to get dirty media items for saving.
 * Used by MediaCenterDataLoader to register with ServiceContext.
 *
 * Flow:
 * 1. If media center is busy → return [] (skip media in this save cycle)
 * 2. If no dirty order items → return [] (nothing to save)
 * 3. If dirty items exist:
 *    - Get FLC media list
 *    - Find matching records by MED_VW_KEY
 *    - Merge MEDIA_DISPLAY_ORDER from media center into FLC record
 *    - Clear dirty flags in media center
 *    - Return array of merged records
 */
export const useMediaV2Save = () => {
  const logger = useMediaLogger('MediaV2Save');

  // Get FLC media and update function
  const { media: flcMedia, updateMediaByCategory } = useMediaResource();

  // Get media center store state
  const { isBusy } = useMediaStoreReadonly();
  const items = useMediaStore((state) => state.items);
  const dirtyOrder = useMediaStore((state) => state.dirtyOrder);
  const clearDirtyOrder = useMediaStore((state) => state.clearDirtyOrder);

  /**
   * Get dirty media items for saving.
   * Returns array of media records with updated MEDIA_DISPLAY_ORDER, or empty array.
   */
  const getDirtyMediaForSave = useCallback((): any[] => {
    // If busy, skip media in this save cycle
    if (isBusy) {
      logger.log('Media center is busy, skipping media save');
      return [];
    }

    // Get dirty keys as array
    const dirtyKeys = Array.from(dirtyOrder);

    // If no dirty items, nothing to save
    if (dirtyKeys.length === 0) {
      logger.log('No dirty order items, skipping media save');
      return [];
    }

    logger.log(`Found ${dirtyKeys.length} dirty order items`);

    // Group dirty items by category for the update
    const dirtyByCategory = new Map<string, any[]>();

    // Build the dirty records by merging MEDIA_DISPLAY_ORDER from media center into FLC records
    const dirtyRecords: any[] = [];

    for (const key of dirtyKeys) {
      // Find the item in media center store (has the updated MEDIA_DISPLAY_ORDER)
      const mediaCenterItem = items.find((item) => item.MED_VW_KEY === key);
      if (!mediaCenterItem) {
        logger.warn(`Dirty item ${key} not found in media center store`);
        continue;
      }

      // Find the matching FLC record
      const flcRecord = (flcMedia as any[]).find(
        (record) => record.MED_VW_KEY === key,
      );
      if (!flcRecord) {
        logger.warn(`Dirty item ${key} not found in FLC media`);
        continue;
      }

      // Merge only MEDIA_DISPLAY_ORDER from media center into FLC record
      const mergedRecord = {
        ...flcRecord,
        MEDIA_DISPLAY_ORDER: mediaCenterItem.MEDIA_DISPLAY_ORDER,
      };

      dirtyRecords.push(mergedRecord);

      // Group by category for updateMediaByCategory
      const category = mediaCenterItem.MEDIA_CATEGORY;
      if (!dirtyByCategory.has(category)) {
        dirtyByCategory.set(category, []);
      }
      dirtyByCategory.get(category)!.push(mergedRecord);
    }

    // Update FLC state by category (so UI stays in sync)
    for (const [category, records] of dirtyByCategory) {
      // Get all FLC records for this category
      const categoryRecords = (flcMedia as any[]).filter(
        (record) => record.MEDIA_CATEGORY === category,
      );

      // Update the records that are dirty
      const updatedCategoryRecords = categoryRecords.map((record) => {
        const dirtyRecord = records.find(
          (r) => r.MED_VW_KEY === record.MED_VW_KEY,
        );
        return dirtyRecord || record;
      });

      // Determine the MediaCategory enum value
      let mediaCategory: MediaCategory | MediaCategory[];
      if (category === 'Photo') {
        mediaCategory = MediaCategory.Photo;
      } else if (category === 'Document') {
        mediaCategory = MediaCategory.Document;
      } else if (category === 'Floor Plan') {
        mediaCategory = MediaCategory.FloorPlan;
      } else if (category === 'Tours') {
        mediaCategory = MediaCategory.Tour;
      } else {
        logger.warn(`Unknown category: ${category}`);
        continue;
      }

      logger.log(
        `Updating FLC state for ${category} with ${records.length} dirty items`,
      );
      updateMediaByCategory({
        category: mediaCategory,
        resources: updatedCategoryRecords,
      });
    }

    // Clear dirty flags in media center
    logger.log('Clearing dirty order flags');
    clearDirtyOrder();

    logger.log(`Returning ${dirtyRecords.length} dirty records for save`);
    return dirtyRecords;
  }, [
    isBusy,
    dirtyOrder,
    items,
    flcMedia,
    updateMediaByCategory,
    clearDirtyOrder,
    logger,
  ]);

  return {
    getDirtyMediaForSave,
  };
};
